import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { ODOO } from "./crmV2Helpers";

const pad2 = (n) => String(n).padStart(2, "0");
const keyOf = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const buildMonth = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const leading = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < leading; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
};

const weekNum = (d) => {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  return Math.ceil((((t - yearStart) / 86400000) + 1) / 7);
};

const MiniCal = ({ date, selected, onSelect, onMonthChange }) => {
  const weeks = buildMonth(date);
  const todayKey = keyOf(new Date());
  const label = date.toLocaleString("en-US", { month: "long", year: "numeric" });
  return (
    <div style={{ width: 220, flexShrink: 0, padding: 12, borderLeft: `1px solid ${ODOO.border}` }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <button type="button" onClick={() => onMonthChange(-1)} style={navBtn}><ChevronLeft size={14} /></button>
        <span style={{ fontSize: 12, fontWeight: 700 }}>{label}</span>
        <button type="button" onClick={() => onMonthChange(1)} style={navBtn}><ChevronRight size={14} /></button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, textAlign: "center", fontSize: 10, color: ODOO.muted, marginBottom: 4 }}>
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <div key={i}>{d}</div>)}
      </div>
      {weeks.map((week, wi) => (
        <div key={wi} style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
          {week.map((cell, ci) => {
            if (!cell) return <div key={ci} style={{ height: 26 }} />;
            const k = keyOf(cell);
            const isSel = k === selected;
            const isToday = k === todayKey;
            const inMonth = cell.getMonth() === date.getMonth();
            return (
              <button
                key={ci}
                type="button"
                onClick={() => onSelect(cell)}
                style={{
                  height: 26,
                  border: isToday || isSel ? "2px solid #dc3545" : "none",
                  borderRadius: "50%",
                  background: inMonth ? "#e8f4fc" : "transparent",
                  fontSize: 11,
                  fontWeight: isToday || isSel ? 700 : 500,
                  color: inMonth ? ODOO.text : "#adb5bd",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                {cell.getDate()}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
};

const navBtn = {
  border: "none",
  background: "transparent",
  cursor: "pointer",
  padding: 4,
  display: "inline-flex",
  color: ODOO.muted,
};

const leadDateKey = (l) => {
  const raw = l.date_deadline || l.next_activity?.date_deadline || l.date_open;
  if (!raw) return null;
  const d = new Date(raw);
  if (isNaN(d)) return String(raw).slice(0, 10);
  return keyOf(d);
};

const CrmV2CalendarView = ({ leads, loading, onLeadClick }) => {
  const [calDate, setCalDate] = useState(() => new Date());
  const [selected, setSelected] = useState(() => keyOf(new Date()));
  const [miniDate, setMiniDate] = useState(() => new Date());

  const weeks = useMemo(() => buildMonth(calDate), [calDate]);
  const todayKey = keyOf(new Date());
  const monthLabel = calDate.toLocaleString("en-US", { month: "long", year: "numeric" });

  const byDay = useMemo(() => {
    const map = {};
    for (const l of leads) {
      const k = leadDateKey(l);
      if (!k) continue;
      (map[k] || (map[k] = [])).push(l);
    }
    return map;
  }, [leads]);

  return (
    <div style={{ display: "flex", background: "#fff", borderTop: `1px solid ${ODOO.border}`, minHeight: "calc(100vh - 200px)" }}>
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 16px", borderBottom: `1px solid ${ODOO.border}`, flexWrap: "wrap" }}>
          <button type="button" onClick={() => setCalDate(new Date(calDate.getFullYear(), calDate.getMonth() - 1, 1))} style={{ ...navBtn, border: `1px solid ${ODOO.border}`, borderRadius: 3 }}>
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            onClick={() => { const n = new Date(); setCalDate(n); setSelected(keyOf(n)); setMiniDate(n); }}
            style={{
              border: `1px solid ${ODOO.border}`,
              background: "#fff",
              borderRadius: 3,
              padding: "5px 12px",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.04em",
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            TODAY
          </button>
          <button type="button" onClick={() => setCalDate(new Date(calDate.getFullYear(), calDate.getMonth() + 1, 1))} style={{ ...navBtn, border: `1px solid ${ODOO.border}`, borderRadius: 3 }}>
            <ChevronRight size={16} />
          </button>
          <span style={{ fontSize: 13, fontWeight: 600, color: ODOO.muted, marginLeft: 4 }}>MONTH</span>
          {loading && <span style={{ fontSize: 12, color: ODOO.muted }}>Loading…</span>}
        </div>

        <div style={{ flex: 1, overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", minHeight: "100%" }}>
            <thead>
              <tr>
                <th style={{ width: 36, borderBottom: `1px solid ${ODOO.border}`, background: "#fafbfa" }} />
                {["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map((d) => (
                  <th key={d} style={{ padding: "8px 4px", fontSize: 12, fontWeight: 600, color: ODOO.muted, borderBottom: `1px solid ${ODOO.border}`, background: "#fafbfa", textAlign: "center" }}>
                    {d}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map((week, wi) => {
                const first = week.find(Boolean);
                return (
                  <tr key={wi}>
                    <td style={{ border: `1px solid ${ODOO.borderLight}`, textAlign: "center", fontSize: 11, color: ODOO.muted, verticalAlign: "top", paddingTop: 8 }}>
                      {first ? weekNum(first) : ""}
                    </td>
                    {week.map((cell, ci) => {
                      if (!cell) {
                        return <td key={ci} style={{ border: `1px solid ${ODOO.borderLight}`, background: "#fafbfa", height: 100 }} />;
                      }
                      const k = keyOf(cell);
                      const dayLeads = byDay[k] || [];
                      const isToday = k === todayKey;
                      const isSel = k === selected;
                      return (
                        <td
                          key={ci}
                          onClick={() => setSelected(k)}
                          style={{
                            border: `1px solid ${ODOO.borderLight}`,
                            verticalAlign: "top",
                            height: 100,
                            padding: 4,
                            background: "#fff",
                            cursor: "pointer",
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
                            <span
                              style={{
                                width: 26,
                                height: 26,
                                borderRadius: "50%",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 12,
                                fontWeight: 700,
                                border: isToday || isSel ? "2px solid #dc3545" : "none",
                                color: ODOO.text,
                              }}
                            >
                              {cell.getDate()}
                            </span>
                          </div>
                          {dayLeads.slice(0, 3).map((l) => (
                            <button
                              key={l.id}
                              type="button"
                              onClick={(e) => { e.stopPropagation(); onLeadClick?.(l); }}
                              style={{
                                display: "block",
                                width: "100%",
                                textAlign: "left",
                                fontSize: 10.5,
                                padding: "2px 5px",
                                borderRadius: 2,
                                border: "none",
                                marginBottom: 2,
                                background: "#e8f4fc",
                                color: ODOO.text,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                cursor: "pointer",
                                fontFamily: "inherit",
                                fontWeight: 600,
                              }}
                            >
                              {l.name}
                            </button>
                          ))}
                          {dayLeads.length > 3 && (
                            <div style={{ fontSize: 10, color: ODOO.muted }}>+{dayLeads.length - 3}</div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ display: "none" }}>{monthLabel}</div>
      </div>

      <MiniCal
        date={miniDate}
        selected={selected}
        onSelect={(d) => {
          setSelected(keyOf(d));
          setCalDate(new Date(d.getFullYear(), d.getMonth(), 1));
          setMiniDate(new Date(d.getFullYear(), d.getMonth(), 1));
        }}
        onMonthChange={(delta) => setMiniDate(new Date(miniDate.getFullYear(), miniDate.getMonth() + delta, 1))}
      />
    </div>
  );
};

export default CrmV2CalendarView;
